import { Link } from "@inertiajs/react"
import { GraduationCap, Plus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card, CardDescription, CardTitle } from "@/components/ui/card"
import { newMasterclassPath } from "@/routes"

/**
 * OKS-54 — Empty state shown on `Brand/Masterclasses/Index` when the
 * brand has zero masterclasses (PRD AC11 + UX Spec §3 Empty).
 *
 * Pattern follows `ux-states` skill — icon + headline + body + primary
 * CTA. Distinct from the per-tab empty state (e.g. Draft tab w/ 0
 * drafts) which renders inline and intentionally has NO CTA (the
 * header "Create masterclass" CTA stays visible — see Index page).
 *
 * `can_create` gate is owned by the page wrapper: this component
 * always renders the CTA when mounted (page-level gate keeps the
 * empty state honest about the actor's permissions).
 */
export function MasterclassEmptyState() {
  const { t } = useTranslation()

  return (
    <Card
      className="flex flex-col items-center justify-center gap-4 py-12 text-center"
      data-testid="masterclasses-empty-state"
    >
      <GraduationCap
        className="text-muted-foreground size-12"
        aria-hidden="true"
      />
      <CardTitle>{t("masterclasses.index.empty.title")}</CardTitle>
      <CardDescription className="max-w-md">
        {t("masterclasses.index.empty.body")}
      </CardDescription>
      <Button asChild data-testid="masterclasses-empty-state-cta">
        <Link href={newMasterclassPath()}>
          <Plus className="mr-1 size-4" aria-hidden="true" />
          {t("masterclasses.index.empty.cta")}
        </Link>
      </Button>
    </Card>
  )
}
